import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const LogAccordionItem = ({ date, logs, screenType }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate summary for this date
  const summary = logs.reduce(
    (acc, log) => {
      const time = parseInt(log.total_time);
      acc.total_time += time;
      if (log.status === "online") {
        acc.online_time += time;
      }
      return acc;
    },
    { online_time: 0, total_time: 0 }
  );

  // Convert minutes to formatted time string
  const formatTime = (minutes) => {
    const mins = parseInt(minutes);
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;

    if (hours > 0 && remainingMins > 0) {
      return `${hours}h ${remainingMins}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${remainingMins}m`;
    }
  };

  // Format UTC date and time
  const formatUTCDate = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split("T")[0]; // YYYY-MM-DD format
  };

  // Get color based on state/status
  const getStatusColor = (type, value) => {
    if (screenType === "vehicle") {
      if (type === "state") {
        return (
          {
            app_moves: "bg-emerald-100 text-emerald-800",
            app_not_moves: "bg-blue-100 text-blue-800",
            app_stopped: "bg-rose-100 text-rose-800",
            default: "bg-gray-100 text-gray-800",
          }[value] || "bg-gray-100 text-gray-800"
        );
      } else {
        return value === "online"
          ? "bg-emerald-100 text-emerald-800"
          : "bg-rose-100 text-rose-800";
      }
    } else {
      return value === "online"
        ? "bg-emerald-100 text-emerald-800"
        : "bg-rose-100 text-rose-800";
    }
  };

  return (
    <motion.div
      className="border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-white"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.005 }}
    >
      {/* UTC Time Highlight */}
      <div className="bg-black text-white px-4 py-2 flex items-center justify-between text-xs">
        <div className="flex items-center space-x-2">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>UTC Time: {formatUTCDate(date)}</span>
        </div>
        <div className="flex items-center space-x-3">
          <span className="flex items-center space-x-1">
            <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
            <span>Online: {formatTime(summary.online_time)}</span>
          </span>
          <span className="flex items-center space-x-1">
            <span className="w-2 h-2 rounded-full bg-gray-400"></span>
            <span>Total: {formatTime(summary.total_time)}</span>
          </span>
        </div>
      </div>

      <motion.button
        className="w-full text-left bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 px-6 py-4 flex justify-between items-center transition-all"
        onClick={() => setIsExpanded(!isExpanded)}
        whileTap={{ scale: 0.98 }}
      >
        <div className="flex items-center space-x-4">
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
            className="flex-shrink-0"
          >
            <svg
              className="w-5 h-5 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </motion.div>
          <div>
            <h3 className="font-semibold text-gray-800 text-lg">
              {new Date(date).toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </h3>
            <p className="text-sm text-gray-500">
              {logs.length} session{logs.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-8">
          <div className="text-right">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">
              Local Online
            </p>
            <p className="font-semibold text-emerald-600 text-lg">
              {formatTime(summary.online_time)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">
              Local Total
            </p>
            <p className="font-semibold text-gray-800 text-lg">
              {formatTime(summary.total_time)}
            </p>
          </div>
        </div>
      </motion.button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{
              opacity: 1,
              height: "auto",
              transition: {
                opacity: { duration: 0.2 },
                height: { duration: 0.3 },
              },
            }}
            exit={{
              opacity: 0,
              height: 0,
              transition: {
                opacity: { duration: 0.1 },
                height: { duration: 0.2 },
              },
            }}
            className="overflow-hidden"
          >
            <div className="divide-y divide-gray-100">
              {logs.map((log, logIdx) => (
                <motion.div
                  key={logIdx}
                  className="grid grid-cols-12 gap-4 p-6 hover:bg-gray-50 transition-colors"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: {
                      delay: logIdx * 0.05,
                      type: "spring",
                      stiffness: 300,
                      damping: 20,
                    },
                  }}
                  exit={{ opacity: 0 }}
                  whileHover={{ scale: 1.01 }}
                >
                  <div className="col-span-12 sm:col-span-6 md:col-span-3">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">
                      Time Range (UTC)
                    </p>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-800">
                        {log.start_time}
                      </span>
                      <span className="text-gray-400">→</span>
                      <span className="font-medium text-gray-800">
                        {log.end_time}
                      </span>
                    </div>
                  </div>

                  <div className="col-span-6 sm:col-span-3 md:col-span-2">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">
                      Duration
                    </p>
                    <p className="font-medium text-indigo-600">
                      {formatTime(log.total_time)}
                    </p>
                  </div>

                  <div className="col-span-6 sm:col-span-3 md:col-span-3">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">
                      State
                    </p>
                    <motion.span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                        "state",
                        log.state
                      )}`}
                      whileHover={{ scale: 1.05 }}
                    >
                      {log.state.replace(/_/g, " ")}
                    </motion.span>
                  </div>

                  {screenType === "vehicle" && (
                    <div className="col-span-12 sm:col-span-6 md:col-span-4">
                      <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">
                        Connection
                      </p>
                      <motion.span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          "status",
                          log.status
                        )}`}
                        whileHover={{ scale: 1.05 }}
                      >
                        {log.status}
                      </motion.span>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default LogAccordionItem;
